/**
 * The map view: composition, and the state its pieces share.
 *
 * Ported from frontend/legacy/index.html, which held this in eleven module-level
 * `let`s mutated from 84 functions. The rule here is that React owns the state
 * and MapLibre is written to from effects — never the other way round — so each
 * fact lives in exactly one place.
 *
 * `/valencia` is the same page in city mode. The legacy version learned that
 * from a server-injected `window.__CITY_MODE__` inline script; this reads the
 * pathname instead, which removes the last inline script from the site and is
 * what lets the CSP drop 'unsafe-inline'.
 */

import type { Feature, FeatureCollection } from "geojson";
// Type-only, and imported rather than left to the global the package also
// declares: the handler signature should not depend on a UMD global being in
// scope.
import type maplibregl from "maplibre-gl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError, api } from "../api/client";
import type { MunicipioSummary } from "../api/client";
import { Combobox } from "../ui/Combobox";
import { BuildingDetail } from "./BuildingDetail";
import { DistritoPanel } from "./DistritoPanel";
import { DistritoPopup } from "./DistritoPopup";
import { MenuGroup } from "./Dropdown";
import { FilterBar } from "./FilterBar";
import type { Layers } from "./LayerMenu";
import { LayerMenu } from "./LayerMenu";
import { MapLegend } from "./MapLegend";
import { RankingModal } from "./RankingModal";
import { SummaryPanel } from "./SummaryPanel";
import { TramoPopup } from "./TramoPopup";
import { ViewSwitch } from "./ViewSwitch";
import type { Anchor } from "./HoverCard";
import { getBounds } from "./lib/bounds";
import { computeBreaks, rampFor } from "./lib/choropleth";
import type { DistritoProps, Metric, RentaMeta } from "./lib/distritos";
import { metricForVista, num } from "./lib/distritos";
import type { BuildingFilters } from "./lib/filter";
import { filterFeatures, isUnfiltered } from "./lib/filter";
import { LETTERS } from "./lib/letters";
import { str } from "./lib/props";
import type { AggregateVista, Vista } from "./lib/vista";
import { VISTAS, VISTA_LABELS, isAggregate } from "./lib/vista";
import { useDistritoLabels } from "./useDistritoLabels";
import {
  EMPTY, LAYERS, highlightDistrito, paintDistritos, setBuildingPaint,
  selectArea, setDistritoFill, setLayerVisible, showBuildings, showDistritos,
  showTramos,
  useMapLibre,
} from "./useMapLibre";

/** The city the district view is pinned to. Mirrors distritos.MUNICIPIO. */
const CITY_MUNICIPIO = "València";

/** True on /valencia, false on /. */
export function isCityMode(pathname: string): boolean {
  return pathname.replace(/\/+$/, "") === "/valencia";
}

/**
 * Views this deployment has no boundaries for.
 *
 * Empty: both layers ship. Kept as the mechanism rather than deleted, because a
 * boundary set can be absent from a deployment — the backend answers 404 for a
 * layer whose file is missing — and the control is built to say so on itself
 * rather than to be hidden. A reader who cannot find Barrios should learn the
 * data is missing here, not conclude the city has none.
 */
const UNAVAILABLE_VISTAS: readonly Vista[] = [];

/** A feature under the cursor, with where to draw its card. */
interface Hovered {
  anchor: Anchor;
  props: Record<string, unknown>;
}

export function MapPage() {
  const { containerRef, ready, mapRef } = useMapLibre();

  const [municipios, setMunicipios] = useState<string[]>([]);
  const [municipio, setMunicipio] = useState<string | null>(null);
  const [geojson, setGeojson] = useState<FeatureCollection | null>(null);
  const [summary, setSummary] = useState<MunicipioSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<BuildingFilters>({
    letters: new Set(LETTERS),
    deadline: null,
  });
  /** The clicked building's reference, or null when the summary is showing. */
  const [selected, setSelected] = useState<string | null>(null);

  const cityMode = isCityMode(window.location.pathname);

  const [layers, setLayers] = useState<Layers>({ tramos: false, paint: "letra" });

  /**
   * How the city view aggregates the stock.
   *
   * Districts by default in city mode: the choropleth is the reason that route
   * exists, and opening on a plain map would make /valencia look like / with the
   * search box missing. Everywhere else there is nothing below the municipality,
   * so the map is always Plano and the switch is not rendered.
   */
  const [vista, setVista] = useState<Vista>(cityMode ? "distritos" : "plano");

  const [tramos, setTramos] = useState<FeatureCollection | null>(null);
  const [renta, setRenta] = useState<RentaMeta | null>(null);
  /**
   * The metric the choropleth paints, or null for the boundaries alone.
   *
   * Null by default. The map opens showing the buildings' own A–G colours with
   * the area limits drawn over them, which is the reading the page is actually
   * about; a ramp on top tints the very colours the reader came to compare, and
   * choosing it should be a decision rather than a state to undo.
   */
  const [metric, setMetric] = useState<Metric | null>(null);
  // Income has no barrio-level figure, so the barrio view offers eight metrics
  // rather than nine. Derived rather than stored: a selection left behind by a
  // view change would paint a layer the legend cannot describe.
  const activeMetric = metricForVista(metric, vista);
  const [distrito, setDistrito] = useState<string | null>(null);
  /**
   * Which sidebar section is open.
   *
   * Three fixed sections, not two with a shifting label: "Barrios" and
   * "Distritos" are different readings a reader compares, and a strip where one
   * of them is only reachable by first changing the map made the other look
   * like it did not exist.
   */
  const [tab, setTab] = useState<"ciudad" | AggregateVista>("ciudad");
  const [ranking, setRanking] = useState(false);
  const [tramoHover, setTramoHover] = useState<Hovered | null>(null);
  const [distritoHover, setDistritoHover] = useState<Hovered | null>(null);

  // Guards every setState that follows an await. Without it, switching
  // municipality mid-request lets the slower response overwrite the faster one,
  // and the map shows one place while the sidebar describes another.
  const requestSeq = useRef(0);

  useEffect(() => {
    api.municipios()
      .then(setMunicipios)
      .catch(() => setError("No se ha podido cargar la lista de municipios."));
  }, []);

  const load = useCallback(async (nom: string) => {
    const seq = ++requestSeq.current;
    setMunicipio(nom);
    setLoading(true);
    setError(null);
    setSummary(null);
    // The previous municipality's streets must not stay on the map under the
    // new one's buildings: they are a different town's network.
    setTramos(null);
    // Nor its buildings. Beyond the obvious — one town's polygons drawn under
    // another town's name — this is what made the map stop centring on the
    // second and every later search: `municipio` changed one render before the
    // new GeoJSON arrived, so the fit below ran against the OLD features,
    // framed the OLD town, and recorded the NEW name as already fitted. The
    // real data then arrived to a viewport that had used up its one fit.
    setGeojson(null);

    try {
      // In parallel: the summary is small and the GeoJSON is megabytes, so
      // awaiting them in sequence would leave the sidebar blank for the whole
      // download.
      const [features, stats] = await Promise.all([
        api.municipioGeojson(nom),
        api.municipioSummary(nom),
      ]);
      if (seq !== requestSeq.current) return;
      setGeojson(features);
      setSummary(stats);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setGeojson(null);
      setError(err instanceof ApiError
        ? err.message
        : `No se han podido cargar los edificios de ${nom}.`);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  // City mode loads València itself; there is no search box to click.
  useEffect(() => {
    if (cityMode) void load(CITY_MUNICIPIO);
  }, [cityMode, load]);

  const visible = useMemo<Feature[]>(() => {
    if (!geojson) return [];
    return isUnfiltered(filters, LETTERS)
      ? geojson.features
      : filterFeatures(geojson.features, filters);
  }, [geojson, filters]);

  // The one place the buildings source is written to. Refits only when the
  // municipality changes, not on every filter toggle: refitting on a filter
  // yanks the viewport away from whatever the user was looking at.
  const fittedFor = useRef<string | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const fit = municipio !== null && fittedFor.current !== municipio;
    showBuildings(
      map,
      geojson ? { type: "FeatureCollection", features: visible } : EMPTY,
      { fit },
    );
    // Only once something was actually framed. An empty collection cannot be
    // fitted to, so recording the name there would spend the fit on nothing.
    if (fit && visible.length > 0) fittedFor.current = municipio;
  }, [ready, mapRef, geojson, visible, municipio]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && ready) setBuildingPaint(map, layers.paint);
  }, [ready, mapRef, layers.paint]);

  // ── Road segments ───────────────────────────────────────────────────────
  // Fetched on demand rather than with the municipality: it is a second
  // multi-megabyte payload, and most readers never turn the overlay on.
  useEffect(() => {
    if (!layers.tramos || !municipio || tramos) return;
    let cancelled = false;
    api.municipioTramos(municipio)
      .then((data) => { if (!cancelled) setTramos(data); })
      .catch(() => {
        if (!cancelled) setError(`No se ha podido cargar la red viaria de ${municipio}.`);
      });
    return () => { cancelled = true; };
  }, [layers.tramos, municipio, tramos]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    showTramos(map, tramos ?? EMPTY);
    setLayerVisible(map, LAYERS.tramosLine, layers.tramos);
    // The card describes a segment that is no longer drawn.
    if (!layers.tramos) setTramoHover(null);
  }, [ready, mapRef, tramos, layers.tramos]);

  // ── Districts (city mode) ───────────────────────────────────────────────
  // Whichever granularity the view is showing. Fetched on demand and kept, so
  // switching back and forth costs nothing after the first look at each.
  const [areas, setAreas] = useState<Record<string, FeatureCollection>>({});
  useEffect(() => {
    if (!cityMode || !isAggregate(vista) || areas[vista]) return;
    let cancelled = false;
    const fetcher = vista === "barrios" ? api.barriosGeojson : api.distritosGeojson;
    fetcher()
      .then((data) => {
        if (cancelled) return;
        setAreas((prev) => ({ ...prev, [vista]: data }));
        // The income meta rides on the collection rather than in a second
        // request; it is not part of the GeoJSON spec, so it is read off the
        // payload explicitly instead of being pretended into the type. Absent
        // on the barrio layer, which is why this clears rather than keeps.
        setRenta((data as unknown as { renta?: RentaMeta }).renta ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setError(vista === "barrios"
            ? "No se han podido cargar los barrios."
            : "No se han podido cargar los distritos.");
        }
      });
    return () => { cancelled = true; };
  }, [cityMode, vista, areas]);

  // What the map and the panel are currently describing. Null in Plano, and
  // null while a layer is still in flight — never the previous granularity's
  // features under the new one's legend.
  const distritos = isAggregate(vista) ? areas[vista] ?? null : null;

  // The income block only exists on the district payload, so a view change has
  // to re-read it rather than leave the last one standing.
  useEffect(() => {
    if (vista !== "distritos") setRenta(null);
  }, [vista]);

  // An open area belongs to one layer: its key names a district or a barrio,
  // never both. Carried across a view change it would leave the panel showing a
  // district's figures under a map of barrios.
  useEffect(() => { setDistrito(null); }, [vista]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && ready && distritos) showDistritos(map, distritos);
  }, [ready, mapRef, distritos]);

  // Classification is derived, never stored: breaks that outlived their metric
  // are how the legacy legend ended up printing one metric's cuts over another
  // metric's ramp.
  const breaks = useMemo(
    () => (activeMetric
      ? computeBreaks(
        (distritos?.features ?? []).map((f) => num(f.properties?.[activeMetric.key])))
      : []),
    [distritos, activeMetric],
  );
  const ramp = useMemo(
    () => rampFor(breaks, activeMetric?.lowFirst), [breaks, activeMetric],
  );

  useEffect(() => {
    const map = mapRef.current;
    if (map && ready && distritos && activeMetric) {
      paintDistritos(map, activeMetric.key, breaks, ramp);
    }
  }, [ready, mapRef, distritos, activeMetric, breaks, ramp]);

  useEffect(() => {
    const map = mapRef.current;
    // Any aggregate view, not just districts. Keyed to `vista === "distritos"`
    // this painted no choropleth at all in the barrio view — 88 outlines over a
    // bare basemap, with the metric the panel was ranking by nowhere on the map.
    //
    // With no metric chosen the fill goes too, and setDistritoFill darkens and
    // thickens the outlines to carry the boundary reading on their own.
    const paint = cityMode && isAggregate(vista) && activeMetric !== null;
    if (map && ready) setDistritoFill(map, paint);
  }, [ready, mapRef, cityMode, vista, activeMetric]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && ready) selectArea(map, distrito ?? "");
  }, [ready, mapRef, distrito]);

  /** Open one district: ring it, frame it, and show it in the sidebar. */
  const openDistrito = useCallback((key: string) => {
    setDistrito(key);
    if (isAggregate(vista)) setTab(vista);
    // An explicit request for the district view, so any open building detail
    // closes rather than sitting under a panel describing something else.
    setSelected(null);

    const map = mapRef.current;
    const feature = distritos?.features.find((f) => str(f.properties?.["key"]) === key);
    if (!map || !feature) return;
    const bounds = getBounds([feature]);
    if (bounds) map.fitBounds(bounds, { padding: 60, maxZoom: 15.5 });
  }, [mapRef, distritos, vista]);

  useDistritoLabels(
    mapRef.current, ready, cityMode ? distritos?.features ?? null : null,
    distrito, openDistrito,
  );

  /** Cursor position in the map container's own coordinates. */
  const anchorAt = useCallback((e: maplibregl.MapMouseEvent): Anchor | null => {
    const box = containerRef.current?.getBoundingClientRect();
    if (!box) return null;
    return {
      x: e.originalEvent.clientX - box.left,
      y: e.originalEvent.clientY - box.top,
      containerWidth: box.width,
    };
  }, [containerRef]);

  /**
   * Every pointer interaction with the map, bound once.
   *
   * Registered against layers rather than the canvas, so MapLibre does the
   * hit-testing and a click on the basemap falls through instead of clearing the
   * panel — the legacy page bound to the map and had to filter the features
   * itself, which is why clicking the sea sometimes closed the panel and
   * sometimes did not.
   *
   * The handlers only ever call setState. Reading `properties` is the one place
   * the map's data crosses into React.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    const onBuildingClick = (e: maplibregl.MapLayerMouseEvent) => {
      const id = e.features?.[0]?.properties?.["id"];
      if (typeof id === "string" && id) setSelected(id);
    };
    // The cursor is the only affordance that a building is clickable at all.
    const enterBuilding = () => { map.getCanvas().style.cursor = "pointer"; };
    const leaveBuilding = () => { map.getCanvas().style.cursor = ""; };

    const enterTramo = () => { map.getCanvas().style.cursor = "crosshair"; };
    const moveTramo = (e: maplibregl.MapLayerMouseEvent) => {
      const props = e.features?.[0]?.properties;
      const anchor = anchorAt(e);
      if (props && anchor) setTramoHover({ anchor, props });
    };
    const leaveTramo = () => {
      map.getCanvas().style.cursor = "";
      setTramoHover(null);
    };

    const moveDistrito = (e: maplibregl.MapLayerMouseEvent) => {
      const props = e.features?.[0]?.properties;
      const anchor = anchorAt(e);
      if (!props || !anchor) return;
      map.getCanvas().style.cursor = "pointer";
      highlightDistrito(map, LAYERS.distritosHover, str(props["key"]) ?? "");
      setDistritoHover({ anchor, props });
    };
    const leaveDistrito = () => {
      map.getCanvas().style.cursor = "";
      highlightDistrito(map, LAYERS.distritosHover, "");
      setDistritoHover(null);
    };
    const clickDistrito = (e: maplibregl.MapLayerMouseEvent) => {
      const key = str(e.features?.[0]?.properties?.["key"]);
      if (key) openDistrito(key);
    };

    map.on("click", LAYERS.buildingsFill, onBuildingClick);
    map.on("mouseenter", LAYERS.buildingsFill, enterBuilding);
    map.on("mouseleave", LAYERS.buildingsFill, leaveBuilding);
    map.on("mouseenter", LAYERS.tramosLine, enterTramo);
    map.on("mousemove", LAYERS.tramosLine, moveTramo);
    map.on("mouseleave", LAYERS.tramosLine, leaveTramo);
    map.on("mousemove", LAYERS.distritosFill, moveDistrito);
    map.on("mouseleave", LAYERS.distritosFill, leaveDistrito);
    map.on("click", LAYERS.distritosFill, clickDistrito);

    return () => {
      map.off("click", LAYERS.buildingsFill, onBuildingClick);
      map.off("mouseenter", LAYERS.buildingsFill, enterBuilding);
      map.off("mouseleave", LAYERS.buildingsFill, leaveBuilding);
      map.off("mouseenter", LAYERS.tramosLine, enterTramo);
      map.off("mousemove", LAYERS.tramosLine, moveTramo);
      map.off("mouseleave", LAYERS.tramosLine, leaveTramo);
      map.off("mousemove", LAYERS.distritosFill, moveDistrito);
      map.off("mouseleave", LAYERS.distritosFill, leaveDistrito);
      map.off("click", LAYERS.distritosFill, clickDistrito);
    };
  }, [ready, mapRef, anchorAt, openDistrito]);

  // A building from the previous municipality must not stay open over the new
  // one: the panel would describe a building that is no longer on the map.
  useEffect(() => { setSelected(null); }, [municipio]);

  /** A row in the ranking is a way in to that municipality, with its network on. */
  const pickFromRanking = useCallback((nom: string) => {
    setRanking(false);
    setLayers((l) => ({ ...l, tramos: true }));
    void load(nom);
  }, [load]);

  return (
    <>
      <header className="topbar">
        <div className="brand">
          re·k <span className="brand-sub">
            {cityMode ? "València · distritos" : "rehabilitación CV"}
          </span>
        </div>

        {cityMode
          ? <h1 className="city-title">{CITY_MUNICIPIO}</h1>
          : (
            <Combobox
              options={municipios}
              label="Municipio"
              placeholder="Busca tu municipio…"
              onSelect={(nom) => void load(nom)}
              disabled={municipios.length === 0}
            />
          )}

        {/* Only where there is something below the municipality to aggregate
            by. Outside València the map is always Plano, and a switch with two
            dead options would be asking a question with one answer. */}
        {cityMode && (
          <ViewSwitch
            vista={vista}
            onChange={(next) => {
              setVista(next);
              // A sidebar section describing an aggregation the map no longer
              // paints has to yield. A reader sitting on Ciudad is left there:
              // the city summary is true under every view, so dragging them off
              // it would undo a choice they made.
              if (next === "plano") setTab("ciudad");
              else setTab((t) => (t === "ciudad" ? t : next));
            }}
            unavailable={UNAVAILABLE_VISTAS}
          />
        )}

        <MenuGroup className="filterbar">
          <FilterBar filters={filters} onChange={setFilters} disabled={!geojson} />
          <LayerMenu layers={layers} onChange={setLayers} disabled={!geojson} />
        </MenuGroup>

        <button type="button" className="ranking-btn" onClick={() => setRanking(true)}>
          ⚡ Ranking redes
        </button>

        {/* Polite: the status changes while the citizen is reading the map, and
            an assertive region would interrupt them on every load. */}
        <span className="status" role="status" aria-live="polite">
          {error
            ?? (loading
              ? `Cargando ${municipio}…`
              : municipio
                ? `${visible.length.toLocaleString("es-ES")} edificios`
                : "selecciona un municipio")}
        </span>

        <a href="/ciudadano" className="nav-cta">Para ciudadanos →</a>
      </header>

      <div className="main">
        <div className="map" ref={containerRef}>
          <MapLegend
            paint={layers.paint}
            distrito={cityMode && isAggregate(vista) && distritos && activeMetric
              ? { metric: activeMetric, breaks, ramp, vista }
              : null}
            tramos={layers.tramos}
          />
          {layers.tramos && (
            <TramoPopup anchor={tramoHover?.anchor ?? null}
                        props={tramoHover?.props ?? null} />
          )}
          {cityMode && isAggregate(vista) && (
            <DistritoPopup anchor={distritoHover?.anchor ?? null}
                           props={(distritoHover?.props ?? null) as DistritoProps | null}
                           metric={activeMetric} />
          )}
        </div>

        <aside className="sidebar">
          {/* Two things to see only in the city view; elsewhere the strip would
              be one tab, which is not a choice. */}
          {cityMode && (
            <div className="sidebar-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={tab === "ciudad"}
                className={tab === "ciudad" ? "side-tab active" : "side-tab"}
                // The city summary is true under any view, so this one never
                // touches the map.
                onClick={() => { setTab("ciudad"); setSelected(null); }}
              >
                Ciudad
              </button>

              {VISTAS.filter(isAggregate).map((v) => (
                <button
                  key={v}
                  type="button"
                  role="tab"
                  aria-selected={tab === v}
                  className={tab === v ? "side-tab active" : "side-tab"}
                  onClick={() => {
                    setTab(v);
                    setSelected(null);
                    // The panel ranks the areas the map is painting: opening
                    // Barrios over a district choropleth would list rows whose
                    // keys are not in the layer a click would have to zoom to.
                    setVista(v);
                  }}
                >
                  {VISTA_LABELS[v]}
                </button>
              ))}
            </div>
          )}

          {/* The detail replaces the summary rather than stacking below it: the
              sidebar is one column, and the legacy page's two stacked panels meant
              the building you had just clicked was often below the fold. */}
          {selected
            ? (
              <BuildingDetail
                localId={selected}
                onClose={() => setSelected(null)}
              />
            )
            : cityMode && tab !== "ciudad" && distritos
              ? (
                <DistritoPanel
                  features={distritos.features}
                  renta={renta}
                  vista={tab}
                  metric={activeMetric}
                  onMetric={setMetric}
                  selected={distrito}
                  onSelect={openDistrito}
                  onBack={() => setDistrito(null)}
                  breaks={breaks}
                />
              )
              : (
                <SummaryPanel
                  municipio={municipio}
                  summary={summary}
                  visible={visible.length}
                  loading={loading}
                />
              )}
        </aside>
      </div>

      <RankingModal
        open={ranking}
        onClose={() => setRanking(false)}
        onPick={pickFromRanking}
      />
    </>
  );
}
